import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, Zap, RefreshCcw } from 'lucide-react';
import { NotificationBell } from '../components/NotificationBell.jsx';
import { OnlineUsers } from '../components/OnlineUsers.jsx';
import { NAV_GROUPS } from '../config/navigation.js';

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

function useCurrentPage() {
  const location = useLocation();
  return (
    ALL_ITEMS.find((item) =>
      item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)
    ) || { label: 'Feed ERP', icon: Zap }
  );
}

function useTheme() {
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute('data-theme') || 'dark'
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setTheme(document.documentElement.getAttribute('data-theme') || 'dark')
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return theme;
}

export default function Topbar({ onOpenMenu }) {
  const theme = useTheme();
  const page = useCurrentPage();
  const PageIcon = page.icon;

  useEffect(() => {
    document.title = `${page.label} · Feed ERP`;
    return () => { document.title = 'Feed · Gestão Unificada'; };
  }, [page.label]);

  const handleClearCache = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) registration.unregister();
      });
    }
    if (window.caches) {
      caches.keys().then(names => {
        for (let name of names) caches.delete(name);
      });
    }
    window.location.href = window.location.pathname + '?refresh=' + new Date().getTime();
  };

  return (
    <header
      className={[
        'sticky top-0 z-20 flex items-center gap-3 h-16 px-4',
        'relative overflow-hidden',
        theme === 'light' ? 'topbar-bg-light' : 'topbar-bg-dark',
      ].join(' ')}
    >
      {/* Linha animada na borda inferior */}
      <div className="header-glow-line" aria-hidden="true" />

      {/* Toggle mobile */}
      <button
        onClick={onOpenMenu}
        className="lg:hidden topbar-action-btn grid place-items-center w-9 h-9 shrink-0"
        aria-label="Abrir menu"
      >
        <Menu size={20} />
      </button>

      {/* Ícone + título da página atual */}
      <div className="flex items-center gap-2.5 flex-1 min-w-0 lg:pl-1">
        {PageIcon && (
          <span
            className="hidden sm:grid topbar-page-icon place-items-center w-8 h-8 shrink-0"
            aria-hidden="true"
          >
            <PageIcon size={16} strokeWidth={2} />
          </span>
        )}
        <h2 className="text-base sm:text-lg font-700 animated-gradient-text tracking-tight truncate select-none">
          {page.label}
        </h2>
      </div>

      {/* Lado direito */}
      <div className="flex items-center gap-2.5 shrink-0">
        <button
          onClick={handleClearCache}
          className="topbar-action-btn grid place-items-center w-9 h-9 shrink-0 text-text-muted hover:text-text transition-colors"
          title="Limpar Cache e Recarregar"
          aria-label="Limpar Cache"
        >
          <RefreshCcw size={16} />
        </button>
        <OnlineUsers theme={theme} />
        <div className="topbar-divider hidden sm:block" aria-hidden="true" />
        <NotificationBell className="topbar-action-btn relative grid place-items-center w-9 h-9" />
      </div>
    </header>
  );
}
